import { useEffect, useState } from "react"
import { ShieldCheck } from "lucide-react"
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import {
	Button,
	PaywallDialog,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator,
} from "@/components"
import { api } from "../system/api.js"
import { getSymbolCurrency } from "../system/util.js"
import { getStripePromise } from "../system/stripePromise.js"
import { paywallTrigger, usePaywall } from "../system/paywallTrigger.js"
import { buildStripeAppearance } from "../system/stripeAppearance.js"
import { STATIC_PLAN_DATA } from "../data/Plans.js"

// Monthly billing only — the annual cycle is gone with the new commercial
// model, so the picker no longer offers a Monthly/Annually switch.
const BILLING = "monthly"

export default function AppPaywallHost() {
	const { open } = usePaywall()

	const [plans, setPlans] = useState([])
	const [plansBySlug, setPlansBySlug] = useState({})
	const [currentPlanId, setCurrentPlanId] = useState(null)
	const [currencySymbol, setCurrencySymbol] = useState("$")
	const [selectedPlanId, setSelectedPlanId] = useState(null)
	const [succeeded, setSucceeded] = useState(false)
	const [loadError, setLoadError] = useState(null)
	const [loading, setLoading] = useState(false)

	const [stripePromise, setStripePromise] = useState(null)
	const [clientSecret, setClientSecret] = useState(null)
	const [checkoutProduct, setCheckoutProduct] = useState(null)
	const [checkoutPaymentMethods, setCheckoutPaymentMethods] = useState([])
	const [checkoutLoading, setCheckoutLoading] = useState(false)
	const [checkoutError, setCheckoutError] = useState(null)

	const resetCheckoutState = () => {
		setStripePromise(null)
		setClientSecret(null)
		setCheckoutProduct(null)
		setCheckoutPaymentMethods([])
		setCheckoutError(null)
		setCheckoutLoading(false)
	}

	useEffect(() => {
		if (!open) return
		let cancelled = false

		// eslint-disable-next-line react-hooks/set-state-in-effect
		setLoading(true)
		setLoadError(null)
		setSelectedPlanId(null)
		setSucceeded(false)
		resetCheckoutState()

		api("subscription/change/list")
			.then((res) => {
				if (cancelled) return
				const products = res.products || []

				const bySlug = {}
				products.forEach((row) => {
					if (row.slug === "free") return
					if (!bySlug[row.slug]) bySlug[row.slug] = {}
					if (row.billingCycle === "monthly") bySlug[row.slug].monthly = row
					if (row.billingCycle === "yearly") bySlug[row.slug].yearly = row
					if (row.action === "current") bySlug[row.slug].currentAction = "current"
				})

				const slugs = Object.keys(bySlug)
				const currentSlug =
					slugs.find((slug) => bySlug[slug].currentAction === "current") || null

				const mapped = slugs
					.map((slug) => {
						const entry = bySlug[slug]
						const monthlyRow = entry.monthly
						const yearlyRow = entry.yearly
						const sample = monthlyRow || yearlyRow
						const action = monthlyRow?.action || yearlyRow?.action
						const planData = STATIC_PLAN_DATA[slug]
						// Subscribers are moving between tiers; free users are
						// picking one for the first time.
						const ctaLabel =
							action === "downgrade"
								? `Downgrade to ${sample.name}`
								: currentSlug
									? `Upgrade to ${sample.name}`
									: `Choose ${sample.name}`
						const monthlyPrice = monthlyRow ? Number(monthlyRow.price) : 0
						const yearlyPrice = yearlyRow ? Number(yearlyRow.price) : undefined
						return {
							id: slug,
							name: sample.name,
							tagline: planData?.tagline,
							priceMonthly: monthlyPrice,
							priceAnnualPerMonth: yearlyPrice !== undefined ? yearlyPrice / 12 : undefined,
							priceAnnualTotal: yearlyPrice,
							usage: planData?.usage,
							features: planData?.list || [],
							ctaLabel,
							popular: planData?.popular ?? false,
						}
					})
					.sort((a, b) => a.priceMonthly - b.priceMonthly)

				const currencyRow = products[0]
				const symbol = currencyRow ? getSymbolCurrency(currencyRow.currency) : "$"

				setPlans(mapped)
				setPlansBySlug(bySlug)
				setCurrentPlanId(currentSlug)
				setCurrencySymbol(symbol)
			})
			.catch((err) => {
				if (cancelled) return
				if (err.errorCode === "CANCELATION_PENDING") {
					setLoadError(
						"Your subscription is pending cancellation. You cannot change plans until the cancellation is completed, but you may revert the cancellation at any time."
					)
				} else {
					setLoadError("Failed to load subscription plans.")
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [open])

	const getRowForSelection = (slug, cycle) => {
		const entry = plansBySlug[slug]
		if (!entry) return null
		const key = cycle === "annual" ? "yearly" : "monthly"
		return entry[key] || entry.monthly || entry.yearly || null
	}

	const handleSelectPlan = async (plan) => {
		resetCheckoutState()
		if (!plan) {
			setSelectedPlanId(null)
			return
		}
		setSelectedPlanId(plan.id)
		const row = getRowForSelection(plan.id, BILLING)
		if (!row) {
			setCheckoutError("Selected plan is unavailable. Please try a different option.")
			return
		}
		if (row.action === "upgrade") {
			setCheckoutLoading(true)
			try {
				const res = await api("payment/initiate", { type: "upgrade", id: parseInt(row.pid) })
				setCheckoutProduct(res.product)
				setCheckoutPaymentMethods(res.paymentMethods || [])
				if (res.stripePublicKey) setStripePromise(getStripePromise(res.stripePublicKey))
				if (res.clientSecret) setClientSecret(res.clientSecret)
			} catch (err) {
				setCheckoutError(
					err.errorCode === "INVOICE_ALREADY_PAID"
						? "This invoice has already been paid"
						: err.errorCode === "INVOICE_STATUS_INVALID"
							? "This invoice cannot be paid at this time"
							: "Failed to initiate payment"
				)
			} finally {
				setCheckoutLoading(false)
			}
		}
	}

	const handleOpenChange = (next, stage) => {
		if (next) return
		setSelectedPlanId(null)
		resetCheckoutState()
		paywallTrigger.close()
		if (stage === "success") {
			window.location.reload()
		}
	}

	if (loadError && open) {
		return (
			<PaywallDialog
				open
				onOpenChange={(next) => {
					if (!next) {
						setLoadError(null)
						paywallTrigger.close()
					}
				}}
				plans={[]}
				pickerTitle="Change plan"
				pickerDescription={loadError}
				showBillingToggle={false}
			/>
		)
	}

	return (
		<PaywallDialog
			open={open && !loading}
			onOpenChange={handleOpenChange}
			plans={plans}
			pickerTitle={currentPlanId ? "Change plan" : "Pick a plan"}
			pickerDescription={
				currentPlanId
					? "Pick a tier that fits how you build."
					: "Unlock the full feature set. Cancel anytime."
			}
			showBillingToggle={false}
			billing={BILLING}
			selectedPlanId={selectedPlanId}
			onSelectPlan={handleSelectPlan}
			succeeded={succeeded}
			onSucceededChange={setSucceeded}
			currencySymbol={currencySymbol}
			currentPlanId={currentPlanId}
			checkoutTitle={(plan) => {
				const row = getRowForSelection(plan.id, BILLING)
				if (row?.action === "downgrade") return `Downgrade to ${plan.name}`
				return `Upgrade to ${plan.name}`
			}}
			checkoutDescription="Confirm your selection to continue."
			backToPickerLabel="← Back to plans"
			successHeading="You're subscribed"
			successDescription={(plan) => (
				<>
					You&apos;re on {plan?.name ?? "your new plan"}. Close this window to refresh your billing details.
				</>
			)}
			checkoutSlot={({ plan, onSuccess }) => (
				<ChangePlanCheckoutSlot
					plan={plan}
					row={getRowForSelection(plan.id, BILLING)}
					billing={BILLING}
					currencySymbol={currencySymbol}
					stripePromise={stripePromise}
					clientSecret={clientSecret}
					product={checkoutProduct}
					paymentMethods={checkoutPaymentMethods}
					loading={checkoutLoading}
					loadError={checkoutError}
					onLoadErrorClear={() => setCheckoutError(null)}
					onSuccess={() => {
						setSucceeded(true)
						onSuccess()
					}}
				/>
			)}
		/>
	)
}

function ChangePlanCheckoutSlot({
	plan,
	row,
	billing,
	currencySymbol,
	stripePromise,
	clientSecret,
	product,
	paymentMethods,
	loading,
	loadError,
	onLoadErrorClear,
	onSuccess,
}) {
	if (!row) {
		return <p className="text-caption text-danger">Selected plan is unavailable.</p>
	}

	if (row.action === "downgrade") {
		return (
			<DowngradeConfirmSlot
				plan={plan}
				row={row}
				currencySymbol={currencySymbol}
				onSuccess={onSuccess}
			/>
		)
	}

	if (loading) {
		return <div className="h-32 rounded-md border border-border bg-card shimmer-bar" />
	}

	if (loadError) {
		return (
			<div className="rounded-md border border-border bg-card p-5 flex flex-col gap-3">
				<p className="text-caption text-danger">{loadError}</p>
				<Button
					variant="outline"
					size="sm"
					className="text-label w-fit"
					onClick={onLoadErrorClear}
				>
					Dismiss
				</Button>
			</div>
		)
	}

	if (!stripePromise || !clientSecret || !product) {
		return <div className="h-32 rounded-md border border-border bg-card shimmer-bar" />
	}

	const order = buildCheckoutOrder({ plan, row, billing, currencySymbol, product })

	return (
		<Elements
			stripe={stripePromise}
			options={{
				clientSecret,
				appearance: buildStripeAppearance(),
				loader: "auto",
			}}
		>
			<UpgradeCheckoutForm
				row={row}
				paymentMethods={paymentMethods}
				order={order}
				onSuccess={onSuccess}
			/>
		</Elements>
	)
}

function DowngradeConfirmSlot({ plan, row, currencySymbol, onSuccess }) {
	const [processing, setProcessing] = useState(false)
	const [error, setError] = useState(null)

	const handleConfirm = async () => {
		setProcessing(true)
		setError(null)
		try {
			await api("subscription/downgrade", { pid: row.pid })
			onSuccess()
		} catch (err) {
			setError(
				err.errorCode === "PRODUCT_CHANGE_NOT_ALLOWED"
					? "This plan change is not allowed due to currency or billing cycle restrictions."
					: "Failed to downgrade subscription."
			)
			setProcessing(false)
		}
	}

	const priceText =
		row.billingCycle === "yearly"
			? `${currencySymbol}${row.price} / year`
			: `${currencySymbol}${row.price} / month`

	return (
		<div className="rounded-md border border-border bg-card p-5 flex flex-col gap-4 max-w-xl">
			<div>
				<h3 className="text-section text-foreground font-semibold">
					Downgrade to {plan.name}?
				</h3>
				<p className="text-body text-muted-foreground mt-1">
					Your plan will switch to{" "}
					<span className="text-foreground font-medium">{plan.name}</span> at {priceText}. The
					change applies immediately.
				</p>
			</div>
			{error && <p className="text-caption text-danger">{error}</p>}
			<div className="flex gap-2">
				<Button
					variant="destructive"
					size="sm"
					className="text-label"
					onClick={handleConfirm}
					disabled={processing}
				>
					{processing ? "Processing..." : "Confirm downgrade"}
				</Button>
			</div>
		</div>
	)
}

function UpgradeCheckoutForm({ row, paymentMethods, order, onSuccess }) {
	const stripe = useStripe()
	const elements = useElements()

	const [processing, setProcessing] = useState(false)
	const [error, setError] = useState(null)
	const [selectedMethod, setSelectedMethod] = useState(
		paymentMethods.length > 0 ? paymentMethods[0].id : "new"
	)

	const handleSubmit = async (e) => {
		e.preventDefault()
		if (!stripe || !elements) return

		setProcessing(true)
		setError(null)

		try {
			let paymentData = {}

			if (selectedMethod === "new") {
				const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
					elements,
					confirmParams: {
						return_url: window.location.origin + "/checkout/complete",
					},
					redirect: "if_required",
				})

				if (confirmError) {
					if (confirmError.type === "card_error" || confirmError.type === "validation_error") {
						setError(confirmError.message)
					} else {
						setError("The payment could not be processed. Please try again.")
					}
					setProcessing(false)
					return
				}

				if (paymentIntent?.status !== "succeeded") {
					setError("The payment was not successful. Please try again.")
					setProcessing(false)
					return
				}

				paymentData = {
					paymentIntentId: paymentIntent.id,
					paymentMethodId: paymentIntent.payment_method,
				}
			} else {
				paymentData = { paymentMethodId: selectedMethod }
			}

			await api("payment/confirm", {
				type: "upgrade",
				id: parseInt(row.pid),
				paymentData,
			})

			onSuccess()
		} catch (err) {
			setError(err.message || "Payment failed. Please try again.")
			setProcessing(false)
		}
	}

	const paymentMethodOptions = [
		...paymentMethods.map((m) => ({
			value: m.id,
			label: `${m.brand.charAt(0).toUpperCase() + m.brand.slice(1)} ending in ${m.last4} (Exp: ${String(m.exp_month).padStart(2, "0")}/${m.exp_year})`,
		})),
		{ value: "new", label: "Use a new card" },
	]

	return (
		<form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
			<div>
				<h2 className="text-section text-foreground mb-4">Order Summary</h2>
				<div className="bg-card border border-border rounded-md p-5">
					<h3 className="text-section text-foreground font-semibold mb-3">
						{order.summaryTitle}
					</h3>
					<div className="flex flex-col gap-2 text-body">
						{order.summaryRows.map((rowItem, i) => (
							<div key={i} className="flex justify-between">
								<span className="text-muted-foreground">{rowItem.label}</span>
								<span className="text-foreground">{rowItem.value}</span>
							</div>
						))}
					</div>
					<Separator className="my-4" />
					<div className="flex justify-between text-body font-semibold">
						<span className="text-foreground">{order.dueTodayLabel}</span>
						<span className="text-primary text-section">{order.dueTodayPrice}</span>
					</div>
					{order.nextBilling && (
						<div className="flex justify-between text-caption text-muted-foreground mt-1">
							<span>Next billing:</span>
							<span>{order.nextBilling}</span>
						</div>
					)}
				</div>
			</div>

			<div>
				<h2 className="text-section text-foreground mb-4">Payment</h2>
				<div className="bg-card border border-border rounded-md p-5">
					<p className="text-caption text-muted-foreground mb-4">
						Secure checkout. Your card is never stored.
					</p>

					{paymentMethods.length > 0 && (
						<div className="mb-4">
							<label className="text-caption text-muted-foreground mb-1 block">
								Payment method
							</label>
							<Select value={selectedMethod} onValueChange={setSelectedMethod}>
								<SelectTrigger className="w-full bg-muted border-border text-body">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{paymentMethodOptions.map((opt) => (
										<SelectItem key={opt.value} value={opt.value} className="text-body">
											{opt.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{selectedMethod === "new" && (
						<div className="mb-4">
							<PaymentElement options={{ layout: "tabs" }} />
						</div>
					)}

					{error && <p className="text-caption text-danger mb-3">{error}</p>}

					<Button
						type="submit"
						className="w-full text-label"
						disabled={!stripe || processing}
					>
						{processing ? "Processing..." : order.ctaLabel}
					</Button>

					{order.footer && (
						<p className="text-caption text-muted-foreground mt-3">{order.footer}</p>
					)}
				</div>
				<p className="text-caption text-muted-foreground mt-3 font-medium flex items-center gap-1.5">
					<ShieldCheck size={13} strokeWidth={1.5} className="shrink-0" />
					Your payment information is secure and encrypted.
				</p>
			</div>
		</form>
	)
}

function buildCheckoutOrder({ plan, row, billing, currencySymbol, product }) {
	const isAnnual = billing === "annual" || row.billingCycle === "yearly"
	const summaryRows = [
		{ label: "Plan:", value: plan.name },
		{
			label: "Billing:",
			value: isAnnual ? "Annual, auto-renew" : "Monthly, auto-renew",
		},
		{ label: "Cancel:", value: "Anytime" },
	]

	if (product?.isProrata) {
		summaryRows.push({
			label: "Days remaining:",
			value: `${product.daysRemaining} days`,
		})
		summaryRows.push({
			label: "Regular price:",
			value: `${currencySymbol}${product.regularPrice} ${isAnnual ? "/yr" : "/mo"}`,
		})
	}

	const dueTodayPrice = `${currencySymbol}${product?.price ?? row.price}`
	const nextBilling = product?.isProrata
		? `${currencySymbol}${product.regularPrice} on ${new Date(product.nextBillingDate).toLocaleDateString()}`
		: isAnnual
			? `${currencySymbol}${row.price} / year`
			: `${currencySymbol}${row.price} / month`

	const ctaLabel = isAnnual
		? `Subscribe to ${plan.name} - ${currencySymbol}${row.price}/yr`
		: `Subscribe to ${plan.name} - ${currencySymbol}${row.price}/mo`

	const footer = product?.isProrata
		? `You'll pay ${currencySymbol}${product.price} today (pro-rata for ${product.daysRemaining} days remaining). Starting ${new Date(product.nextBillingDate).toLocaleDateString()}, you'll be charged ${currencySymbol}${product.regularPrice} ${isAnnual ? "per year" : "per month"}.`
		: isAnnual
			? `You'll be charged ${currencySymbol}${row.price} today, then yearly. Cancel any time.`
			: `You'll be charged ${currencySymbol}${row.price} today, then ${currencySymbol}${row.price} per month. Cancel any time.`

	return {
		summaryTitle: `${plan.name} plan`,
		summaryRows,
		dueTodayLabel: product?.isProrata ? "Due today (pro-rata):" : "Due today:",
		dueTodayPrice,
		nextBilling,
		ctaLabel,
		footer,
	}
}
